import kebabCase from 'lodash.kebabcase'
import upperFirst from 'lodash/upperFirst'
import { i18n } from '@/i18n'
import store from '@/store/store'
import { getFormFieldTranslationId, getTrueFalseAsYesNo } from '@/helpers/form'
import { formatDate } from '@/helpers/date'
import { formatCurrency } from '@/helpers/number'
import { textList } from '@/helpers'
import { CommissionListSource } from '@/enums/graphql'

export class ActivityLogParser {
    static attributeDataTypes = {}

    /**
     * Parses the activityLog to extract relevant information.
     *
     * @param {Object} activityLog - The activity log.
     * @returns {Object} - The parsed data.
     */
    static parse (activityLog) {
        if (this.preprocess) activityLog = this.preprocess(activityLog)

        const data = {
            causer: null,
            event: activityLog.description,
            subject: i18n.tc(`common.change-log.subject.${activityLog.isSubjectRelation ? 'some--target' : 'this'}.${kebabCase(activityLog.log_name)}`, 1),
            logDetails: [],
        }
        if (activityLog.causer !== null) {
            data.causer = activityLog.causer.consultant?.getContactName({ consultingCompanyName: !activityLog.causer.consultant.worksForUserConsultingCompany }) ?? i18n.t('common.term.someone')
        }

        // TODO @MTR & @TFU: At some point the causer of the ActivityLog can and will be `null`, e.g. shadow processing of commissions. Handle in the creation of the logDescription.
        // Log description & details
        const getLogDescriptionFunctionName = `getLogDescription${upperFirst(activityLog.description)}`
        const getLogDetailsFunctionName = `getLogDetails${upperFirst(activityLog.description)}`

        if (this[getLogDescriptionFunctionName]) data.logDescription = this[getLogDescriptionFunctionName](activityLog, data)
        if (this[getLogDetailsFunctionName]) data.logDetails = this[getLogDetailsFunctionName](activityLog, data)

        return data
    }

    static getLogDescriptionCreated (activityLog, data) {
        const interpolationParams = {
            someone: data.causer,
            something: `<b>${data.subject}</b>`,
        }
        if (data.causer) {
            return i18n.t(`common.change-log.someone-${activityLog.isSubjectRelation ? 'added' : 'created'}-something`, interpolationParams)
        } else {
            return i18n.tc(`common.change-log.something-has-been-${activityLog.isSubjectRelation ? 'added' : 'created'}`, activityLog.attributeKeys.length, interpolationParams)
        }
    }

    static getLogDescriptionUpdated (activityLog, data) {
        const interpolationParams = {
            someone: data.causer,
            something: `${i18n.tc('common.change-log.subject.this.field', activityLog.attributeKeys.length)} <b>${activityLog.attributeLabels}</b>`,
            ofSomething: `${i18n.tc(`common.change-log.subject.of-some.${kebabCase(activityLog.log_name)}`, 1)}`,
        }
        if (data.causer) {
            return i18n.t(`common.change-log.someone-updated-something${activityLog.isSubjectRelation && !this.hideSubjectRelationInLogDescription ? '-of-something' : ''}`, interpolationParams)
        } else {
            return upperFirst(i18n.tc(`common.change-log.something${activityLog.isSubjectRelation && !this.hideSubjectRelationInLogDescription ? '-of-something' : ''}-has-been-updated`, activityLog.attributeKeys.length, interpolationParams))
        }
    }

    static getLogDescriptionDeleted (activityLog, data) {
        const interpolationParams = {
            someone: data.causer,
            something: `<b>${data.subject}</b>`,
        }
        if (data.causer) {
            return i18n.t(`common.change-log.someone-deleted-something`, interpolationParams)
        } else {
            return upperFirst(i18n.tc(`common.change-log.something-has-been-deleted`, activityLog.attributeKeys.length, interpolationParams))
        }
    }

    static composeLogDetails (activityLog, data) {
        const details = []
        const properties = activityLog.properties.attributes ?? activityLog.properties.old ?? {}

        // TODO improvement @TFU: Proper display of attributes that have an ID as value.
        // TODO improvement @TFU: Extract loop body that creates a single `detail` object to separate function.
        Object.keys(properties).forEach(attributeKey => {
            const attributeDataType = this.attributeDataTypes[attributeKey] ?? 'undefined'

            const detail = {
                key: attributeKey,
                label: i18n.tc(getFormFieldTranslationId({ type: 'label', context: kebabCase(activityLog.log_name), key: kebabCase(attributeKey) }), 1),
            }

            const newValue = activityLog.properties?.attributes?.[attributeKey]
            const oldValue = activityLog.properties?.old?.[attributeKey]

            if (this.checkValue(newValue)) detail.newValue = this.getFormattedLogDetailValue(activityLog, attributeKey, attributeDataType, newValue)
            if (this.checkValue(oldValue)) detail.oldValue = this.getFormattedLogDetailValue(activityLog, attributeKey, attributeDataType, oldValue)

            if (detail.newValue || detail.oldValue) details.push(detail)
        })

        return details
    }

    static showLogDetailsCreated = false
    static getLogDetailsCreated (activityLog, data) {
        return this.showLogDetailsCreated ? this.composeLogDetails(activityLog, data) : []
    }

    static showLogDetailsUpdated = true
    static getLogDetailsUpdated (activityLog, data) {
        return this.showLogDetailsUpdated ? this.composeLogDetails(activityLog, data) : []
    }

    static showLogDetailsDeleted = false
    static getLogDetailsDeleted (activityLog, data) {
        return this.showLogDetailsDeleted ? this.composeLogDetails(activityLog, data) : []
    }

    static checkValue (value) {
        return (value !== '' && value !== null && value !== undefined)
    }

    static getFormattedLogDetailValue (activityLog, attributeKey, attributeDataType, value) {
        let correspondenceLanguage
        let translationId

        switch (attributeDataType) {
            case 'addressCategory':
                return store.state.addressCategories[value]
            case 'addressLabel':
                return store.state.addressLabels[value]
            case 'emailAddressCategory':
                return store.state.emailAddressCategories[value]
            case 'emailAddressLabel':
                return store.state.emailAddressLabels[value]
            case 'phoneNumberLabel':
                return store.state.phoneNumberLabels[value]
            case 'commissionListSource':
                return i18n.tc(`views.commission.commission-list.source--${kebabCase(value)}`, 1)
            case 'cancellationStatus':
                return i18n.tc(`common.status.cancellation-status.${kebabCase(value)}`, 1)
            case 'country':
                return store.state.countryLabels[value]
            case 'dispatchType':
                return i18n.tc(`common.document.dispatch-type.${kebabCase(value)}`, 1)
            case 'correspondenceLanguage':
                correspondenceLanguage = store.state.user.tenantSettings.correspondenceLanguages.find(correspondenceLanguage => correspondenceLanguage.id === value)
                return (correspondenceLanguage) ? correspondenceLanguage.name : value
            case 'consultants':
                return textList(value)
            case 'consultingFileType':
                return i18n.tc(`common.consulting-file.type.${kebabCase(value)}`, 1)
            case 'fileType':
                return i18n.tc(`common.file.type.${kebabCase(value)}`, 1)
            case 'role':
                return store.state.roles[value] // Single role
            case 'roles':
                return value.map(roleId => store.state.roles[roleId]).join(', ') // Array of roles
            case 'date':
                return formatDate(value)
            case 'currency':
                return formatCurrency(value)
            case 'boolean':
                return getTrueFalseAsYesNo(value)
            case 'percentage':
                return `${value} %`
            case 'textList':
                return textList(value)
            case 'localized':
                return Object.keys(value).map(locale => {
                    const output = [value[locale]]
                    const systemLanguage = store.state.user.tenantSettings.systemLanguages.find(systemLanguage => systemLanguage.id === locale)
                    if (systemLanguage) output.unshift(`<span class="additional-info">${systemLanguage.name}:</span> `)
                    return output.join('')
                }).join(', ')
            case 'localized:userLanguage':
                return value[i18n.locale]
            default:
                translationId = getFormFieldTranslationId({ type: 'value', context: kebabCase(activityLog.log_name), key: kebabCase(attributeKey), value: value })
                if (i18n.te(translationId)) return i18n.tc(translationId, 1)
                return value
        }
    }
}
